import { Form, Radio } from '@vivaldis/antd-ui'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { WorkPostSheetFormStep } from '../../constants/wpsFormSteps'
import { useForm } from '../../providers/FormProvider'
import { useStep } from '../../providers/StepProvider'
import { PositionWithWPS } from '../../types'
import { StepsProgressHeader } from '../StepsProgressHeader'
import { Overview } from './steps/Overview'
import { Step1 } from './steps/Step1'
import { Step2 } from './steps/Step2'
import { Step3 } from './steps/Step3'
import { Step4 } from './steps/Step4'
import { UploadPDFWPSFormContent } from './UploadPDFWPSFormContent'

// Example of this form on official web-site: https://www.workstationsheet.be/your-workstation-sheet/online-form

interface Props {
  positionsWithWPS?: PositionWithWPS[]
}

export const FormContent = ({ positionsWithWPS }: Props) => {
  const [t] = useTranslation('web_ui')

  const { values } = useForm()
  const { step, setStep } = useStep()

  const { uploadOwnWPS } = values

  // a custom PDF has only one step
  useEffect(() => {
    if (uploadOwnWPS) {
      setStep(WorkPostSheetFormStep.Step1)
    }
  }, [setStep, uploadOwnWPS])

  // show a new step from the top.
  // Scroll the container itself: scrollIntoView() on an element inside the form would cut off the steps header
  // (it's pulled up with a negative margin) and could scroll other ancestors of the side sheet too.
  const topRef = useRef<HTMLDivElement>(null)
  const isFirstRender = useRef(true)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    getScrollParent(topRef.current)?.scrollTo({ top: 0 })
  }, [step])

  const uploadOwnWPSField = (
    <Form.Item
      name="uploadOwnWPS"
      required
      label={t('work_post_sheet_form.uploadOwnWPS.label')}
      tooltip={t('work_post_sheet_form.uploadOwnWPS.tooltip')}
    >
      <Radio.Group name="uploadOwnWPS">
        <Radio name="uploadOwnWPS" value={false}>
          {t('work_post_sheet_form.uploadOwnWPS.options.no')}
        </Radio>
        <Radio name="uploadOwnWPS" value={true}>
          {t('work_post_sheet_form.uploadOwnWPS.options.yes')}
        </Radio>
      </Radio.Group>
    </Form.Item>
  )

  return (
    <>
      <div ref={topRef} />
      <StepsProgressHeader disabled={uploadOwnWPS} />

      {step === WorkPostSheetFormStep.Step1 ? uploadOwnWPSField : null}

      {uploadOwnWPS ? (
        <UploadPDFWPSFormContent />
      ) : (
        <>
          {step === WorkPostSheetFormStep.Step1 ? (
            <Step1 positionsWithWPS={positionsWithWPS} />
          ) : null}
          {step === WorkPostSheetFormStep.Step2 ? <Step2 /> : null}
          {step === WorkPostSheetFormStep.Step3 ? <Step3 /> : null}
          {step === WorkPostSheetFormStep.Step4 ? <Step4 /> : null}
          {step === WorkPostSheetFormStep.Overview ? <Overview /> : null}
        </>
      )}
    </>
  )
}

// closest ancestor that scrolls (e.g. the side sheet body), or the page
const getScrollParent = (element: HTMLElement | null): Element | null => {
  let parent = element?.parentElement
  while (parent) {
    const { overflowY } = window.getComputedStyle(parent)
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      parent.scrollHeight > parent.clientHeight
    ) {
      return parent
    }
    parent = parent.parentElement
  }
  return document.scrollingElement
}
